/** biome-ignore-all lint/suspicious/noConsole: package initialization logs when Datadog credentials are missing */
/** biome-ignore-all lint/performance/noBarrelFile: package entrypoint intentionally re-exports the public API */
/** biome-ignore-all lint/performance/noReExportAll: package entrypoint intentionally re-exports the public API */

import { Environment } from '@cobliteam/react-environments';
import { datadogRum } from '@datadog/browser-rum';

export * from './DatadogTrack';
export * from './types';

const env = Environment.getInstance().getEnvironment();

export const DEFAULT_ALLOWED_TRACING_URLS = ['https://api.cobli.co', 'https://api-beta.cobli.co'];

/**
 * Odometer 404 is expected when a vehicle has no odometer data in the maintenance API.
 * We discard these resource events so they don't inflate RUM error counts.
 */
function isOdometer404ResourceEvent(event: { type?: string; resource?: { status_code?: number; url?: string } }): boolean {
  if (event.type !== 'resource' || event.resource?.status_code !== 404) {
    return false;
  }
  const url = event.resource.url ?? '';
  return url.includes('api.cobli.co') && url.includes('/maintenance/vehicles/') && url.includes('/odometer');
}

/**
 * Cross-origin scripts loaded without CORS headers produce opaque "Script error."
 * with no stack trace. After analysis (Dec 2025 – May 2026) we confirmed 4.8M
 * occurrences (79% of all RUM errors) originate from browser extensions and
 * third-party injections (Opera, Samsung Internet, ad-blockers) concentrated in
 * ~330 users. These are not actionable — filtering is an industry best practice
 * recommended by Datadog, Sentry, and the broader error-monitoring community.
 */
function isOpaqueScriptError(event: { type?: string; error?: { message?: string; stack?: string } }): boolean {
  if (event.type !== 'error') {
    return false;
  }
  const message = event.error?.message ?? '';
  const stack = event.error?.stack ?? '';
  return message.includes('Script error') && (!stack || stack === 'Error: Script error.\n  at undefined @ ');
}

export default ({ configuration, user }: { configuration: any; user: any }) => {
  const { ddRUMapplicationId, ddRUMclientToken } = env;
  const cfg = configuration ?? {};

  if (!(ddRUMapplicationId && ddRUMclientToken)) {
    console.warn('Datadog RUM AppID and/or Client Token was not found in the ENV vars!');
    return;
  }

  const appBeforeSend = cfg.beforeSend;

  datadogRum.init({
    ...cfg,
    applicationId: ddRUMapplicationId,
    clientToken: ddRUMclientToken,
    site: 'datadoghq.com',
    silentMultipleInit: true,
    allowedTracingOrigins: DEFAULT_ALLOWED_TRACING_URLS,
    beforeSend: (event: any, context: any) => {
      if (isOdometer404ResourceEvent(event)) {
        return false;
      }
      if (isOpaqueScriptError(event)) {
        return false;
      }
      if (typeof appBeforeSend === 'function') {
        return appBeforeSend(event, context);
      }
      return true;
    },
  });
  datadogRum.setUser(user);
  datadogRum.startSessionReplayRecording();
};
